import { addDays, addMonths, isAfter, isBefore, isDate } from "date-fns";
import { utcToZonedTime } from "date-fns-tz";
import { useFlags } from "launchdarkly-react-client-sdk";
import { omit } from "lodash";
import PropTypes from "prop-types";
import React, { useEffect, useMemo, useState } from "react";
import DatePicker from "react-datepicker";
import { useIntl } from "react-intl";
import {
  Checkbox,
  Confirm,
  Divider,
  Dropdown,
  Form,
  Icon,
  Input,
  Message,
  Popup,
  Radio,
  Segment,
  Select
} from "semantic-ui-react";

import { Config } from "../../../../config/api";
import {
  campaignHasPGType,
  currencySymbol,
  formatNumber,
  getClosestTo15Date,
  getMaxAvailableEndDate,
  getTimeZone,
  isDigit,
  parseAPIDateTimeWithTimeZone
} from "../../../../libs/common_utils";
import { useForm } from "../../../../libs/component_utils";
import { freqOptimizationEnabledByGoalType } from "../../../../models/campaign";
import DraftModel from "../../../../models/draft_strategy";
import { Intervals as frequencyIntervals, Types as frequencyTypes } from "../../../../models/enum/frequency";
import { Steps } from "../../../../models/enum/strategy";
import Frequency from "../../../../models/object_value/frequency";
import Goal from "../../../../models/object_value/goal";
import Pacing from "../../../../models/object_value/pacing";
import StrategiesService from "../../../../services/strategy";
import DecimalInput from "../../../common/decimalInput";
import {
  daily_pacing_intervals,
  daily_pacing_options,
  frequency_intervals,
  frequency_options,
  goal_media_options,
  goal_options,
  media_type_options
} from "../../fixtures";
import StrategyManageContext from "../context";
import { NavigationButtonDiv } from "./navigation_buttons";
import { MarketingObjectiveStrategyPlacementTypeField } from "./marketingObjectiveStrategyPlacementTypeField";
import { walmartMXTranslations } from "../../../../translations/walmartMXTranslations";

const BRANDLIFT_MIN_BID = 40;
const BRANDLIFT_MAX_BID = 80;

export const MarketingObjectiveStep = () => {
	const context = React.useContext(StrategyManageContext);
	const intl = useIntl();
	const service = context.services.current.get("strategies");
	const strategy_id = context.strategy_id;
	const is_t1_edit = (service instanceof StrategiesService);
	const {whitelabelChannelDisplay, whitelabelChannelVideo} = useFlags();

	const [loading, setLoading] = useState(true);
	let use_start_cannot_be_ticket = React.useRef(false);
	let use_end_cannot_be_ticket = React.useRef(false);

	const [formSending, setFormSending] = useState(false);
	const [serverError, setServerError] = useState("");

	const { campaign } = context;
	const minTimeInSeconds = Math.min(...campaign.budget_flights.map(f => f.start_date));
	const maxTimeInSeconds = Math.max(...campaign.budget_flights.map(f => f.end_date));
	const minTimeInSecondsTz = Math.min(...campaign.budget_flights.map(f => f.start_date_tz));
	const maxTimeInSecondsTz = Math.min(...campaign.budget_flights.map(f => f.end_date_tz));

	const campaignStartDate = new Date(minTimeInSeconds);
	const campaignStartDateTz = utcToZonedTime(minTimeInSecondsTz, context.campaign.time_zone);
	const campaignEndDate = new Date(maxTimeInSeconds);
	const campaignEndDateTz = utcToZonedTime(maxTimeInSecondsTz, context.campaign.time_zone);
	const currentDateWithTimeZone = parseAPIDateTimeWithTimeZone(new Date(), context.campaign.time_zone);
	const campaignStarted = isBefore(campaignStartDateTz, currentDateWithTimeZone);
	const campaignFinished = isBefore(campaignEndDateTz, currentDateWithTimeZone);
	const minStartDate = isBefore(campaignStartDate, currentDateWithTimeZone) ? currentDateWithTimeZone : campaignStartDate;
	const initialStartDate = !campaignStarted ? campaignStartDate : addDays(getClosestTo15Date(currentDateWithTimeZone), 1);
	const initialEndDate = !campaignFinished ? campaignEndDate : addMonths(getClosestTo15Date(initialStartDate), 1);

	const isEdit = (context.strategy_id > 0);
	const isBrandLiftCampaign = campaign?.brand_lift;
	const [commonData, setCommonData] = useState({
		"goal_types": [],
		"goal_types_loading": false
	});

	let get_continue = React.useRef(true);
	/**
	 * Save local strategy
	 * @param {boolean} is_continue
	 */
	const setOperationType = (is_continue=true) => {
		get_continue.current = is_continue;
	};

	/**
	 * Save local strategy
	 * @param {object} params
	 */
	const addStrategyForm = (params) => {
		(async () => {
			try {
				const strategy = DraftModel.fromJson(
					isBrandLiftCampaign
						? { ...params, strategy_placement_type: "brand_lift" }
						: params
				);
				setFormSending(true);

        let newStrategy = strategy.toJson();
        if(context.campaign.is_pg){
          newStrategy = omit(newStrategy, ['max_bid']);
        }
				const response = await service.save(newStrategy);
				/** @namespace response.data **/

				if(response.data.id > 0) {
					if(is_t1_edit) {
						response.data["t1_id"] = response.data.id;
					}

					context.updateStrategyData(response.data);
					if(get_continue.current) {
						context.stepNavigation.passMarketingObjective(parseInt(response.data.id, 10));
					} else {
						context.strategySuccessfullyUpdated(response.data.strategy_name);
					}
				}
			} catch(e) {
				setServerError(e.error.message);
			} finally {
				setFormSending(false);
			}
		})();
	};

	/**
	 * do a custom validation
	 * @param e
	 * @param {object} fields
	 */
	const postValidation = (e, fields) => {
		let errors = {};

		if(fields?.budget && !DraftModel.budgetIsValid(fields.budget)) {
			errors["budget"] = intl.formatMessage({
				id: "ERROR_BUDGET_LT_MIN",
				defaultMessage: "Budget must be a positive number greater than {min}",
			}, {
				min: DraftModel.BUDGET.MIN
			});
		}

		if(fields?.goal_value) {
			const goal = commonData.goal_types.find(x => x.value === fields.goal_type);
			if(!goal) {
				errors["goal_type"] = intl.formatMessage({
					id: "ERROR_GOAL_TYPE_UNKNOWN",
					defaultMessage: "Unknown goal type was chosen",
				});
			} else if(!Goal.isValid(fields.goal_value, goal.minimum, goal.maximum)) {
				errors["goal_value"] = intl.formatMessage({
					id: "ERROR_GOAL_VALUE_OUTSIDE_MIN_MAX",
					defaultMessage: "Goal value must be a positive number between {min} and {max}",
				}, {
					min: goal.minimum,
					max: goal.maximum,
				});
			}
		}

		if(fields?.pacing_amount && !Pacing.isValid(fields.pacing_amount)) {
			errors["pacing_amount"] = intl.formatMessage({
				id: "ERROR_PACING_OUTSIDE_MIN_MAX",
				defaultMessage: "Pacing amount must be a positive number between {min} and {max}",
			}, {
				min: Pacing.MIN,
				max: Pacing.MAX,
			});
		}

		if(fields?.frequency_amount && !Frequency.isValid(fields.frequency_amount)) {
			errors["frequency_amount"] = intl.formatMessage({
				id: "ERROR_FREQUENCY_AMOUNT_OUTSIDE_MIN_MAX",
				defaultMessage: "Frequency amount must be a positive number between {min} and {max}",
			}, {
				min: Frequency.MIN,
				max: Frequency.MAX,
			});
		}

		if(!fields.use_campaign_start && !isDate(fields.start_date)) {
			errors["start_date"] = intl.formatMessage({
				id: "ERROR_START_DATE_REQUIRED",
				defaultMessage: "Start date is a required field",
			});
		}

		if(!fields.use_campaign_end && !isDate(fields.end_date)) {
			errors["end_date"] = intl.formatMessage({
				id: "ERROR_END_DATE_REQUIRED",
				defaultMessage: "End date is a required field",
			});
		}

		const maxAvailableEndDate = getMaxAvailableEndDate(fields.start_date);
		if (isAfter(fields.end_date, maxAvailableEndDate)) {
			errors["end_date"] = intl.formatMessage({ 
				id: "ERROR_MAX_STRATEGY_DURATION_MONTHS",
				defaultMessage: "Strategy duration should not exceed 36 months",
			});
		}

    if (!is_t1_edit && fields?.strategy_placement_type === "" && !isBrandLiftCampaign) {
			errors["strategy_placement_type"] = intl.formatMessage({
        id: "ERROR_STRATEGY_TYPE_REQUIRED",
        defaultMessage: "Strategy Type is required",
      });
		}

		// bid fields are hidden for spend goals, so don't require them
		if (isBrandLiftCampaign && fields?.goal_type !== "spend") {
      if (fields?.min_bid < BRANDLIFT_MIN_BID) {
        errors["min_bid"] = intl.formatMessage(
          {
            id: "ERROR_MIN_BID_TOO_SMALL",
            defaultMessage: "Min bid must not be less than MXN {min_bid}",
          },
          {
            min_bid: formatNumber(BRANDLIFT_MIN_BID, Config.defaultLocale),
          },
        );
      }

			if (fields?.min_bid > BRANDLIFT_MAX_BID) {
        errors["min_bid"] = intl.formatMessage(
          {
            id: "ERROR_MIN_BID_TOO_LARGE",
            defaultMessage: "Min bid must not be greater than MXN {max_bid}",
          },
          {
            max_bid: formatNumber(BRANDLIFT_MAX_BID, Config.defaultLocale),
          },
        );
      }

      if (fields?.max_bid > BRANDLIFT_MAX_BID) {
        errors["max_bid"] = intl.formatMessage(
          {
            id: "ERROR_MAX_BID_TOO_LARGE",
            defaultMessage: "Max bid must not be greater than MXN {max_bid}",
          },
          {
            max_bid: formatNumber(BRANDLIFT_MAX_BID, Config.defaultLocale),
          },
        );
      }

			if (fields?.max_bid < BRANDLIFT_MIN_BID) {
        errors["max_bid"] = intl.formatMessage(
          {
            id: "ERROR_MAX_BID_TOO_SMALL",
            defaultMessage: "Max bid must not be less than MXN {min_bid}",
          },
          {
            min_bid: formatNumber(BRANDLIFT_MIN_BID, Config.defaultLocale),
          },
        );
      }

			if (!fields?.min_bid) {
				errors["min_bid"] = intl.formatMessage({
					id: "ERROR_MIN_BID_REQUIRED",
        	defaultMessage: "Min bid is required",
				})
			}

			if (!fields?.max_bid) {
				errors["max_bid"] = intl.formatMessage({
					id: "ERROR_MAX_BID_REQUIRED",
					defaultMessage: "Max bid is required",
				})
			}
    }

		if (fields?.min_bid && fields?.max_bid) {
			if (parseFloat(fields.min_bid) > parseFloat(fields.max_bid)) {
				errors["max_bid"] = intl.formatMessage({
					id: "ERROR_MIN_BID_GT_MAX_BID",
					defaultMessage: "Max Bid must be greater than or equal Min Bid",
				});
			}
		}

    return errors;
	};

	/**
	 * Decide which type we should set by default
	 * @param {boolean} displayLaunchDarklyFlag
	 * @param {string} mediaType
	 * @returns {string}
	 */
	const decideWhichMediaTypeToUseByDefault = (displayLaunchDarklyFlag, mediaType) => {
		if(mediaType) {
			return mediaType;
		}

		return (displayLaunchDarklyFlag)? "display" : "video";
	}

  let useCampaignStart = false;
  let useCampaignEnd = false;

  if(!campaignStarted) {
    useCampaignStart = true;
    useCampaignEnd = true;
  } else if(!campaignFinished) {
		useCampaignStart = false;
		useCampaignEnd = true;
	}

	const {values, errors, onChange, onSwitch, loadData, onSubmit, updateValues} = useForm(addStrategyForm, {
		"status": true,
		"campaign_id": context.campaign.id,
		"strategy_name": "",
		"media_type": decideWhichMediaTypeToUseByDefault(whitelabelChannelDisplay, context.strategy["media_type"]),
		"use_campaign_start": useCampaignStart,
		"use_campaign_end": useCampaignEnd,
		"start_date": initialStartDate,
		"end_date": initialEndDate,
		"budget": "",
		"goal_type": context.campaign.is_pg? "spend" : context.campaign.goal_type,
		"goal_value": "",
		"min_bid": "",
		"max_bid": "",
		"bid_aggressiveness": 50,
		"bid_price_is_media_only": !isBrandLiftCampaign,
		"pacing_type": "asap",
		"pacing_interval": "hour",
		"pacing_amount": "",
		"frequency_optimization": true,
		"frequency_type": frequencyTypes.ASAP,
		"frequency_amount": "",
		"frequency_interval": frequencyIntervals.HOUR,
    "strategy_placement_type": "",
	}, postValidation);

	const maxAvailableEndDate = useMemo(() => getMaxAvailableEndDate(values.start_date), [values.start_date]);

	// effect, to control correct state of frequency optimized
	useEffect(() => {
		if(!freqOptimizationEnabledByGoalType(context.campaign.goal_type) && Boolean(values.frequency_optimization)) {
			loadData({
				...values,
				"frequency_optimization": 0
			});
		}
	}, [context.campaign.goal_type]);


	/**
	 * load goal types based on campaign type
	 * @param {string} campaign_type
	 * @param {string} media_type
	 * @return {Promise}
	 */
	const loadGoalTypes = (campaign_type, media_type) => {
		setCommonData({
			...commonData,
			"goal_types_loading": true
		});

		return new Promise((resolve, reject) => {
			context.services.current.get("commonService").getGoalTypes(campaign_type, media_type).then(r => {
				if(campaignHasPGType(context.campaign) && !r.data.find(x => x.value === "spend")) {
					r.data.push({"id": 6, "is_monetary": 1, "maximum": 9999999.9999, "minimum": 0.01, "status": 1, "title": "CPM Spend", "value": "spend"});
				}

				// in case we cannot find selected value for new set of goals let's apply 1st possible value
				if(!r.data.find(x => x.value === values.goal_type)) {
					onChange(null, {"name": "goal_type", "value": r.data[0].value});
				}

				setCommonData({
					...commonData,
					"goal_types": r.data,
					"goal_types_loading": false
				});

				setTimeout(resolve, 30);
			}).catch(e => {
				setCommonData({
					...commonData,
					"goal_types_loading": false
				});
				setTimeout(reject(e), 30);
			});
		})
	};

	// load media type
	useEffect(() => {
		if(loading) {
			return;
		}

		(async() => {
			await loadGoalTypes(context.campaign.goal_type, values.media_type)
		})();
	}, [values.media_type]);

  // watch the value of values.use_campaign_start and values.use_campaign_end
  // for changes to set the right date to display in the date picker
  useEffect(() => {
    const startDate = Boolean(values.use_campaign_start)
      ? campaignStartDate
      : is_t1_edit
        ? new Date(values.start_date)
        : initialStartDate;

    updateValues({ start_date: startDate });
  }, [values.use_campaign_start]);

  useEffect(() => {
    const endDate = Boolean(values.use_campaign_end)
      ? campaignEndDate
      : is_t1_edit
        ? new Date(values.end_date)
        : initialEndDate;

    updateValues({ end_date: endDate });
  }, [values.use_campaign_end]);

	// load data when necessary
	React.useEffect(() => {
		let active = true;
		let promises = [];
		promises.push(loadGoalTypes(context.campaign.goal_type, values.media_type));
		if(isEdit) {
			(async () => {
				try {
					const strategy = DraftModel.fromJson(
						isBrandLiftCampaign
							? { ...context.strategy, strategy_placement_type: "brand_lift" }
							: context.strategy
					);
					let json = strategy.toJson();

					if(!is_t1_edit) {
						json.start_date = json.start_date? getClosestTo15Date(new Date(json.start_date)) : json.use_campaign_start? campaignStartDate : initialStartDate;
						json.end_date = json.end_date? getClosestTo15Date(new Date(json.end_date)) : json.use_campaign_end? campaignEndDate : initialEndDate;
					} else {
						json.start_date = json.start_date? new Date(json.start_date) : json.use_campaign_start? campaignStartDate : initialStartDate;
						json.end_date = json.end_date? new Date(json.end_date) : json.use_campaign_end? campaignEndDate : initialEndDate;
					}
					use_start_cannot_be_ticket.current = campaignStartCannotBeTicked(campaignStarted, Boolean(json.use_campaign_start), json.start_date);
					use_end_cannot_be_ticket.current = campaignEndCannotBeTicked(campaignFinished, Boolean(json.use_campaign_end));

					loadData(Object.assign(values, json));
				} catch (e) {
					console.warn(e);
				}
			})();
		} else {
			use_start_cannot_be_ticket.current = campaignStartCannotBeTicked(campaignStarted, true, values.start_date);
			use_end_cannot_be_ticket.current = campaignEndCannotBeTicked(campaignFinished, true);
		}

		// handle all promises
		Promise.all(promises).catch(e => {
			if (active) setServerError(e.error.message);
		}).finally(() => {
			if (active) setLoading(false);
		});

		return () => {
			active = false;
		};
	}, [strategy_id]);

	React.useLayoutEffect(() => {
		function preventInput(e) {
			if(e.target.validity && e.target.pattern) {
				if (e.target.value.length >= e.target.oldValue.length) {
					let reg = new RegExp(e.target.pattern);
					if (!reg.test(e.target.value)) {
						e.target.value = e.target.oldValue;
						return false;
					}
				}
			}

			e.target.oldValue = e.target.value;
		}

		document.querySelectorAll("input[type='text'][pattern]").forEach(n => {
			n.oldValue = n.value;
			n.addEventListener("input", preventInput);
		});

		return () => {
			document.querySelectorAll("input[type='text'][pattern]").forEach(n => {
				n.removeEventListener("input", preventInput, true);
			});
		}
	}, []);

	/**
	 * handle start date change
	 * @param {Date} date
	 */
	const handleStartDateChange = date => {
		onChange(null, {"name": "start_date", "value": date});
	};

	/**
	 * handle end date change
	 * @param date
	 */
	const handleEndDateChange = date => {
		// In unique cases where the selected end date after the max available end date (by minuites or hours),
      // we should use the max end date to avoid overshooting by hours, minutes or seconds.
		const shouldUseMaxAvailableEndDate = isAfter(date, maxAvailableEndDate);
		onChange(null, {"name": "end_date", "value": shouldUseMaxAvailableEndDate ? maxAvailableEndDate : date});
	};

	/**
	 * handle custom media channel change
	 * @param {object} value
	 */
	const handleMediaChannelChange = value => {
		updateValues(value);
	};

	/**
	 * User cannot tick 'use campaign start date' when it started and not ticked yet
	 * @param {boolean} started
	 * @param {boolean} use_start_ticket
	 * @param {Date} start_date
	 * @return {boolean}
	 */
	const campaignStartCannotBeTicked = (started, use_start_ticket, start_date) => {
		return (!isEdit && started) || (isEdit && started && !use_start_ticket) || (is_t1_edit && isBefore(start_date, currentDateWithTimeZone));
	};

	/**
	 * User cannot tick 'use campaign end date' when it finished and not ticked yet
	 * @param {boolean} finished
	 * @param {boolean} use_end_ticket
	 * @return {boolean}
	 */
	const campaignEndCannotBeTicked = (finished, use_end_ticket) => {
		return (!isEdit && finished) || (isEdit && finished && !use_end_ticket);
	};

	/**
	 * Check that start date cannot be changed
	 * @return {boolean}
	 */
	const startDateCannotBeChanged = () => {
		const checkbox_checked = Boolean(values.use_campaign_start);
		if(checkbox_checked) {
			return true;
    }

		return is_t1_edit && isBefore(values.start_date, currentDateWithTimeZone);
	};

  const filterStartPassedTime = (time) => {
    const selectedDate = new Date(time);
    return minStartDate.getTime() < selectedDate.getTime();
  };

  const filterEndPassedTime = (time) => {
    const selectedStartDate = values.start_date;
    return selectedStartDate.getTime() < time.getTime();
  };


	if(loading) {
		return (<Segment disabled tertiary textAlign="center" className="loading" style={{"height": "300px"}}>
			&nbsp;
		</Segment>);
	}

	return (
		<>
		<Message
			style={{ "marginTop": "10px" }}
			error
			hidden={!serverError}
			size="tiny"
			content={serverError}
		/>
		<Form method="POST" onSubmit={onSubmit} size="small" loading={formSending || commonData.goal_types_loading} noValidate error={!!Object.keys(errors).length} autoComplete="off" data-testid="strategy-wizard-step-marketing-objective">
			<Form.Field inline>
				<label>
					{intl.formatMessage({
						id: "LABEL_STATUS",
						defaultMessage: "Status",
					})}
				</label>
				<Radio
					name="status"
					label={intl.formatMessage({
						id: "STATUS_ACTIVE",
						defaultMessage: "Active",
					})}
					value={1}
					checked={Boolean(values.status)}
					onChange={onChange}
				/>
				<Radio
					style={{ "marginLeft": "15px" }}
					name="status"
					label={intl.formatMessage({
						id: "STATUS_INACTIVE",
						defaultMessage: "Inactive",
					})}
					value={0}
					checked={!Boolean(values.status)}
					onChange={onChange}
				/>
			</Form.Field>
			<Form.Field inline error={errors.hasOwnProperty("strategy_name")} required>
				<label>
					{intl.formatMessage({
						id: "LABEL_STRATEGY_NAME",
						defaultMessage: "Strategy Name",
					})}
				</label>
				<Input
					name="strategy_name"
					data-testid="strategy-name-input"
					required
					minLength="2"
					maxLength="128"
					defaultValue={values.strategy_name}
					onBlur={onChange}
					className="input-field-wider"
				/>
				<div className="custom-error">{errors["strategy_name"]}</div>
			</Form.Field>
      {((!is_t1_edit || values.strategy_placement_type) && !isBrandLiftCampaign) && (
          <MarketingObjectiveStrategyPlacementTypeField
            errors={errors}
            isT1Edit={is_t1_edit}
            isEdit={isEdit}
            onChange={onChange}
          />)
      }
			<MediaTypePicker value={values.media_type}
											 onChange={handleMediaChannelChange}
											 flags={{"display": whitelabelChannelDisplay, "video": whitelabelChannelVideo}}
											 is_t1_edit={is_t1_edit}
											 is_edit={isEdit} />
			<Form.Field inline>
				<label>
					{intl.formatMessage({
						id: "LABEL_TIME_ZONE",
						defaultMessage: "Time Zone",
					})}
				</label>
				<div className="ui labeled input">
					{getTimeZone(context.campaign.time_zone, context.timezones)}
				</div>
			</Form.Field>
			<Form.Group inline width="equal">
				<Form.Field required>
					<label>
						{intl.formatMessage({
							id: "LABEL_START_END_DATE",
							defaultMessage: "Start/End Date",
						})}
						{campaignHasPGType(context.campaign) && (
							<Popup
								inverted
								content={intl.formatMessage({
									id: "HINT_START_END_DATE_PG",
									defaultMessage: "Please make sure the dates match the Programmatic Marketplace agreement",
								})}
								size="mini"
								trigger={
									<Icon
										name="help circle"
										style={{ "position": "relative", "left": "3px" }}
										className="cursor-help"
									/>
								}
							/>
						)}
					</label>
				</Form.Field>
				<Form.Field>
					<Checkbox
						name="use_campaign_start"
						label={intl.formatMessage({
							id: "LABEL_USE_CAMPAIGN_START",
							defaultMessage: "Use campaign start",
						})}
						value={1}
						checked={Boolean(values.use_campaign_start)}
						disabled={use_start_cannot_be_ticket.current}
						onClick={(e, input) => !use_start_cannot_be_ticket.current && onSwitch(e, input)}
					/>
				</Form.Field>
				<Form.Field className="date-picker" disabled={startDateCannotBeChanged()} error={errors.hasOwnProperty("start_date")}>
					<DatePicker
            filterTime={filterStartPassedTime}
						minDate={minStartDate}
						maxDate={values.end_date}
						selected={values.start_date}
						name="start_date"
						onChange={handleStartDateChange}
						showTimeSelect
						timeFormat={Config.timeFormat}
						timeIntervals={15}
						timeCaption="time"
						dateFormat={Config.fullDateTimeFormat}
					/>
					<div className="custom-error" style={{"marginLeft": 0}}>{errors["start_date"]}</div>
				</Form.Field>
				<hr className="date-delimiter" />
				<Form.Field>
					<Checkbox
						name="use_campaign_end"
						label={intl.formatMessage({
							id: "LABEL_USE_CAMPAIGN_END",
							defaultMessage: "Use campaign end",
						})}
						value={1}
						checked={Boolean(values.use_campaign_end)}
						disabled={use_end_cannot_be_ticket.current}
						onClick={(e, input) => !use_end_cannot_be_ticket.current && onSwitch(e, input)}
					/>
				</Form.Field>
				<Form.Field className="date-picker" disabled={Boolean(values.use_campaign_end)} error={errors.hasOwnProperty("end_date")}>
					<DatePicker
            filterTime={filterEndPassedTime}
						name="end_date"
						minDate={values.start_date}
						maxDate={maxAvailableEndDate}
						selected={values.end_date}
						onChange={handleEndDateChange}
						showTimeSelect
						timeFormat={Config.timeFormat}
						timeIntervals={15}
						timeCaption="time"
						dateFormat={Config.fullDateTimeFormat}
					/>
					<div className="custom-error" style={{"marginLeft": 0}}>{errors["end_date"]}</div>
				</Form.Field>
			</Form.Group>
			{!campaignHasPGType(context.campaign) &&
				<Form.Field inline error={errors.hasOwnProperty("budget")}>
					<label>
						{intl.formatMessage({
							id: "LABEL_BUDGET",
							defaultMessage: "Budget",
						})}{" "}{intl.formatMessage({
							id: "LABEL_OPTIONAL",
							defaultMessage: "(optional)",
						})}
					</label>
					<DecimalInput
						type="number"
						name="budget"
						step={0.01}
						places={2}
						defaultValue={values.budget}
						label={currencySymbol(context.campaign.currency_code, context.currencies)}
						autoComplete="off"
						onBlur={onChange}
					/>
					<div className="custom-error">{errors["budget"]}</div>
				</Form.Field>
			}
			<GoalTypeComponent
				onChange={onChange}
				values={values}
				errors={errors}
				loading={commonData.goal_types_loading}
				isPG={campaignHasPGType(context.campaign)}
				options={commonData.goal_types.map(goal => {
					return {
						"text": goal.title,
						"value": goal.value
					}
				})}
				campaign_goal_values={context.campaign.goal_value}
				currency_code={currencySymbol(context.campaign.currency_code, context.currencies)}
				selectDisabled={isBrandLiftCampaign} />
			{values.goal_type !== "spend" && (
				<Form.Field
					inline
					error={
						errors.hasOwnProperty("min_bid") ||
						errors.hasOwnProperty("max_bid") ||
						errors.hasOwnProperty("bid_aggressiveness")
					}
					required={isBrandLiftCampaign}
				>
					<label style={{ position: "relative" }}>
						{intl.formatMessage({
							id: "LABEL_BID",
							defaultMessage: "Bid",
						})}{" "}
						{!isBrandLiftCampaign && intl.formatMessage({
							id: "LABEL_OPTIONAL",
							defaultMessage: "(optional)",
						})}
						<span style={{ position:"absolute", left: "40px" }}>
							{isBrandLiftCampaign && (
							<Popup
								content={walmartMXTranslations.brandLiftStrategyBidTooltipMsg}
								inverted
								size="mini"
								trigger={<Icon name="help circle" className="cursor-help" style={{ fontSize: "0.9em" }} />}
							/>
						)}
						</span>
					</label>
					<span style={{ marginInlineEnd: 10 }}>
						{intl.formatMessage({
							id: "LABEL_MIN_BID_CPM",
							defaultMessage: "Min Bid CPM",
						})}
					</span>
					<DecimalInput
						type="number"
						name="min_bid"
						min={0}
						max={9_999_999.99}
						step={0.01}
						places={2}
						defaultValue={values.min_bid}
						label={currencySymbol(
							context.campaign.currency_code,
							context.currencies
						)}
						autoComplete="off"
						onBlur={onChange}
					/>
					<span style={{ marginInlineStart: 20, marginInlineEnd: 10 }}>
						{intl.formatMessage({
							id: "LABEL_MAX_BID_CPM",
							defaultMessage: "Max Bid CPM",
						})}
					</span>
					<DecimalInput
						type="number"
						name="max_bid"
						min={0}
						max={9_999_999.99}
						step={0.01}
						places={2}
						defaultValue={values.max_bid}
						label={currencySymbol(
							context.campaign.currency_code,
							context.currencies
						)}
						autoComplete="off"
						onBlur={onChange}
					/>
					{values.goal_type === "reach" && (
						<>
							<span style={{ marginInlineStart: 20, marginInlineEnd: 10 }}>
								{intl.formatMessage({ id: "LABEL_BID_FOR", defaultMessage: "for" })}
							</span>
							<Input
								type="number"
								name="bid_aggressiveness"
								required
								min={0}
								max={100}
								defaultValue={values.bid_aggressiveness}
								label="%"
								labelPosition="right"
								autoComplete="off"
								onBlur={onChange}
							/>
							<span style={{ marginInlineStart: 20 }}>
								{intl.formatMessage({ id: "LABEL_BID_OF_IMPRESSIONS", defaultMessage: "of impressions" })}
							</span>
						</>
					)}
					<div className="custom-error">
						{errors["min_bid"] || errors["max_bid"] || errors["bid_aggressiveness"]}
					</div>
				</Form.Field>
			)}
			{!campaignHasPGType(context.campaign) &&
				<><Form.Group inline>
					<Form.Field required>
						<label>
							{intl.formatMessage({
								id: "LABEL_DAILY_PACING",
								defaultMessage: "Daily Pacing",
							})}
						</label>
					</Form.Field>
					<Form.Field className="dropdown__125">
						<Select
							fluid
							name="pacing_type"
							options={daily_pacing_options(intl)}
							onChange={onChange}
							value={values.pacing_type}
						/>
					</Form.Field>
					<Form.Field error={errors.hasOwnProperty("pacing_amount")}>
						<DecimalInput
							type="number"
							name="pacing_amount"
							places={2}
							step={0.01}
							onBlur={onChange}
							required
							defaultValue={values.pacing_amount}
							label={currencySymbol(context.campaign.currency_code, context.currencies)}
							autoComplete="off"
						/>
						<div className="custom-error" style={{"marginLeft": "0"}}>{errors["pacing_amount"]}</div>
					</Form.Field>
					<Form.Field className="dropdown__125">
						<Select
							fluid
							name="pacing_interval"
							options={daily_pacing_intervals(intl)}
							onChange={onChange}
							value={values.pacing_interval}
						/>
					</Form.Field>
				</Form.Group>
				<FrequencyCapComponent
					onChange={onChange}
					values={values}
					errors={errors}
					campaign={context.campaign} /></>
			}
			<Divider hidden />
			<Divider hidden />
			<Divider hidden />
			<NavigationButtonDiv
				loading={loading}
				step={Steps.MARKETING_OBJECTIVES}
				isPG={context.campaign.is_pg}
				onBackClick={() => {}}
				onSave={setOperationType}
				is_t1_edit={service instanceof StrategiesService}
				onCancelClick={context.getBack}
			/>
		</Form>
	</>);
};
MarketingObjectiveStep.propTypes = {
};

const MediaTypePicker = React.memo(({value, is_edit, flags, is_t1_edit, onChange}) => {
	const intl = useIntl();
	const [confirmOpened, setConfirmOpened] = useState(false);
	const cachedInput = React.useRef({});
	const alreadyShowed = React.useRef(false);

	// redesign confirmation buttons
	React.useLayoutEffect(() => {
		if(confirmOpened) {
			document.querySelectorAll("#media_channel_confirm button").forEach(n => {
				n.classList.add("mini");
			});
		}
	}, [confirmOpened]);

	/**
	 * handle changing media type
	 * @param e
	 * @param {object} input
	 */
	const handleChangeMedia = (e, input) => {
		const value = {[input.name]: input.value};
		if(!is_edit || alreadyShowed.current) {
			onChange(value);
		} else {
			cachedInput.current = value;
			setConfirmOpened(true);
		}
	}

	/**
	 * handle changing media type after user confirm change
	 */
	const handleChangeMediaTypeConfirmed = () => {
		alreadyShowed.current = true;
		onChange(cachedInput.current);
		setConfirmOpened(false);
	}

	/**
	 * generate form control
	 * @param {boolean} video
	 * @param {boolean} display
	 * @return {JSX.Element}
	 */
	const formControl = ({video, display}) => {
		if(video && display) {
			return <Select
				name="media_type"
				options={media_type_options(intl)}
				onChange={handleChangeMedia}
				value={value}
				data-testid="strategy-media-type-dropdown" />;
		}

		return (
			<div className="ui labeled input">
				{video
					? intl.formatMessage({
							id: "MEDIA_TYPE_VIDEO",
							defaultMessage: "Video",
					  })
					: intl.formatMessage({
							id: "MEDIA_TYPE_DISPLAY",
							defaultMessage: "Display",
					  })}
			</div>
		);
	}

	return (<Form.Field inline>
		<label>
			{intl.formatMessage({
				id: "LABEL_CHANNEL",
				defaultMessage: "Channel",
			})}
		</label>
		<Confirm
			open={confirmOpened}
			content={intl.formatMessage({
				id: "BODY_CONFIRM_CHANGE_CHANNEL",
				defaultMessage: "Settings that are specific to the Channel, such as supply, targeting, etc will be cleared",
			})}
			cancelButton={intl.formatMessage({
				id: "BTN_CANCEL",
				defaultMessage: "Cancel",
			})}
			confirmButton={intl.formatMessage({
				id: "BTN_OK",
				defaultMessage: "OK",
			})}
			onCancel={() => setConfirmOpened(false)}
			onConfirm={handleChangeMediaTypeConfirmed}
			id="media_channel_confirm"
			size="mini"
		/>
		{!is_t1_edit ? (
			formControl(flags)
		) : (
			<div className="ui labeled input">
				{value === "video"
					? intl.formatMessage({
							id: "MEDIA_TYPE_VIDEO",
							defaultMessage: "Video",
						})
					: intl.formatMessage({
							id: "MEDIA_TYPE_DISPLAY",
							defaultMessage: "Display",
						})}
			</div>
		)}
	</Form.Field>);
}, (prev, next) => {
	/**
	 * render props that necessary to update component
	 * @param {object} props
	 * @return {string}
	 */
	const rendersProps = props => {
		return JSON.stringify({
			"value": props.value,
			"is_edit": props.is_edit,
			"is_t1_edit": props.is_t1_edit
		})
	}
	return rendersProps(prev) === rendersProps(next);
});
MediaTypePicker.propTypes = {
	"value": PropTypes.string.isRequired,
	"is_edit": PropTypes.bool.isRequired,
	"onChange": PropTypes.func.isRequired
};

const FrequencyCapComponent = ({campaign, onChange, errors, values}) => {
	const intl = useIntl();
	let amountIsDisabled = Boolean(values.frequency_optimization) || values.frequency_type === "no-limit";

  const optimizedIsEnabled =
  campaign.goal_type !== "reach" &&
  freqOptimizationEnabledByGoalType(
    campaign.goal_type && values.goal_type
  );

	if(values.frequency_optimization && !optimizedIsEnabled) {
		values.frequency_optimization = false;
		amountIsDisabled = Boolean(values.frequency_optimization) || values.frequency_type === "no-limit";
	}

	return (
		<Form.Group inline>
			<Form.Field required>
				<label>
					{intl.formatMessage({
						id: "LABEL_FREQUENCY_CAP",
						defaultMessage: "Frequency Cap",
					})}
				</label>
			</Form.Field>
			<Form.Field>
				<Radio
					label={intl.formatMessage({
						id: "FREQUENCY_CAP_OPTIMIZED",
						defaultMessage: "Optimized",
					})}
					name="frequency_optimization"
					value={1}
					disabled={!optimizedIsEnabled}
					onClick={(e, n) => optimizedIsEnabled? onChange(e, n) : {}}
					checked={Boolean(values.frequency_optimization)}
				/>
				<Radio
					label={intl.formatMessage({
						id: "FREQUENCY_CAP_CUSTOM",
						defaultMessage: "Custom",
					})}
					name="frequency_optimization"
					value={0}
					onClick={onChange}
					checked={!Boolean(values.frequency_optimization)}
				/>
			</Form.Field>
			<Form.Field className="dropdown__125" disabled={Boolean(values.frequency_optimization)}>
				<Dropdown selection
						  fluid
						  name="frequency_type"
						  options={frequency_options(intl)}
						  onChange={onChange}
						  value={values.frequency_type}
				/>
			</Form.Field>
			<Form.Field disabled={amountIsDisabled} error={errors.hasOwnProperty("frequency_amount")}>
				<Input
					type="number"
					name="frequency_amount"
					required={!amountIsDisabled}
					autoComplete="off"
					onBlur={onChange}
					defaultValue={values.frequency_amount}
				/>
				<div className="custom-error" style={{"marginLeft": "0"}}>{errors["frequency_amount"]}</div>
			</Form.Field>
			<Form.Field className="dropdown__125" disabled={amountIsDisabled}>
				<Dropdown selection
						  fluid
						  name="frequency_interval"
						  options={frequency_intervals(intl)}
						  onChange={onChange}
						  value={values.frequency_interval}
				/>
			</Form.Field>
	</Form.Group>);
};

/**
 * generate goal type component following a few rules
 */
const GoalTypeComponent = ({isPG, currency_code, campaign_goal_values, options, onChange, errors, values, loading=false, selectDisabled = false}) => {
	/**
	 * generate goal value component based on selected goal type
	 * @param {string} goal_type
	 * @param {function} onChange
	 * @param {string} value
	 * @param {string} currency_code
	 * @param {number} campaign_goal_values
	 * @return {JSX.Element}
	 */
	const getGoalValueField = (goal_type, onChange, value, currency_code, campaign_goal_values) => {
		let component;
		switch(goal_type) {
			case("roi"):
				component = <GoalTypeValueROI value={value} campaign_goal_values={campaign_goal_values} onChange={onChange} currency={currency_code} />;
			break;

			case("percent"):
				component = <GoalTypeValuePercent value={value} onChange={onChange} />;
			break;

			case("currency"):
			default:
				component = <GoalTypeValueCurrency value={value} onChange={onChange} currency={currency_code} />;
			break;
		}

		return component;
	};

	const intl = useIntl();

	return (
		<Form.Group inline>
			<Form.Field required>
				<label>
					{intl.formatMessage({
						id: "LABEL_GOAL",
						defaultMessage: "Goal",
					})}
				</label>
			</Form.Field>
			<Form.Field disabled={loading} error={errors.hasOwnProperty("goal_type")}>
				<Dropdown
					selection
					disabled={isPG}
					options={options}
					onChange={onChange}
					placeholder={intl.formatMessage({
						id: "HINT_GOAL_TYPE",
						defaultMessage: "Please select type",
					})}
					value={values.goal_type}
					name="goal_type"
				/>
				<div className="custom-error" style={{"marginLeft": "0"}}>{errors["goal_type"]}</div>
			</Form.Field>
			<Form.Field disabled={loading} error={errors.hasOwnProperty("goal_value")}>
				{getGoalValueField(goal_options.filter(x => x.value === values.goal_type)[0].display,
					onChange,
					values.goal_value.toString(),
					currency_code,
					campaign_goal_values)}
				<div className="custom-error" style={{"marginLeft": "0"}}>{errors["goal_value"]}</div>
			</Form.Field>
			<Form.Field disabled={loading || selectDisabled}>
				<Select
					fluid
					selection
					disabled={isPG || selectDisabled}
					options={goal_media_options(intl)}
					onChange={onChange}
					value={values.bid_price_is_media_only}
					name="bid_price_is_media_only"
				/>
			</Form.Field>
		</Form.Group>
	);
};

const GoalTypeValueROI = ({value, onChange, campaign_goal_values, currency}) => {
	const intl = useIntl();
	let calculated = "--";
	if(isDigit(value) && value > 0) {
		calculated = Number(campaign_goal_values / value).toFixed(2);
	}

	return (
		<>
			<Input
				type="number"
				label={currency}
				name="goal_value"
				required
				defaultValue={value}
				onChange={onChange}
			/>
			{" :1 "}
			{intl.formatMessage(
				{
					id: "BODY_ROI_CALCULATOR",
					defaultMessage: "(equivalent target of {currency} {calculated} CPA)",
				},
				{ currency, calculated }
			)}
		</>
	);
};
GoalTypeValueROI.propTypes = {
	"value": PropTypes.string.isRequired,
	"onChange": PropTypes.func.isRequired,
	"currency": PropTypes.string.isRequired
};

const GoalTypeValueCurrency = ({value, onChange, currency}) => {
	return <><Input
		label={currency}
		type="number"
		name="goal_value"
		required
		defaultValue={value}
		onBlur={onChange}
	/></>
};
GoalTypeValueCurrency.propTypes = {
	"value": PropTypes.string.isRequired,
	"onChange": PropTypes.func.isRequired,
	"currency": PropTypes.string.isRequired
};

const GoalTypeValuePercent = ({value, onChange}) => {
	return <Input
		label="%"
		type="number"
		labelPosition="right"
		name="goal_value"
		required
		defaultValue={value}
		onBlur={onChange}
	/>
};
GoalTypeValuePercent.propTypes = {
	"value": PropTypes.string.isRequired,
	"onChange": PropTypes.func.isRequired,
};
